/* Orden propio de hileras (categorías) y de lo que hay dentro de ellas (canales): funciones puras, sin pantalla ni almacenamiento. */

/** Los ids en el orden guardado; los que no estén guardados (nuevos) van detrás, en su orden de siempre; los guardados que ya no existen se ignoran. */
export function orderIds(ids, saved) {
    const all = (ids || []).map(String);
    const have = new Set(all);
    const first = (Array.isArray(saved) ? saved : []).map(String).filter((id) => have.has(id));
    const placed = new Set(first);
    return first.concat(all.filter((id) => !placed.has(id)));
}

/** Reordena solo una parte de la lista: `subset` (ids visibles, ya en su orden nuevo) ocupa los mismos huecos que tenían en `full`;
    los que no están en `subset` (ocultos por un filtro) no se mueven. → ids de `full` en el orden nuevo. */
export function mergeSubsetOrder(full, subset) {
    const all = (full || []).map(String);
    const sub = (subset || []).map(String).filter((id) => all.indexOf(id) !== -1);
    const inSub = new Set(sub);
    let k = 0;
    return all.map((id) => (inSub.has(id) ? sub[k++] : id));
}

/** Ordena `list` por `saved` según el id que dé `idOf`; estable para lo no guardado. */
export function orderBy(list, idOf, saved) {
    const order = orderIds(list.map(idOf), saved);
    const pos = new Map(order.map((id, i) => [id, i]));
    return list.map((x, i) => [x, i]).sort((a, b) => (pos.get(String(idOf(a[0]))) - pos.get(String(idOf(b[0])))) || a[1] - b[1]).map((x) => x[0]);
}

/** Grupos [{ id, name, items }] en el orden de hileras elegido y, dentro de cada una, los elementos en su orden (order = { cats: [ids], chans: { idGrupo: [ids] } }). */
export function applyGroupOrder(groups, order) {
    const o = order || {};
    return orderBy(groups, (g) => String(g.id), o.cats).map((g) => {
        const chans = o.chans && o.chans[g.id];
        return chans && chans.length ? Object.assign({}, g, { items: orderBy(g.items, (it) => String(it.id), chans) }) : g;
    });
}

/** Todos los elementos de los grupos, uno detrás de otro (para las vistas sin hileras). */
export const flattenGroups = (groups) => groups.reduce((all, g) => all.concat(g.items), []);

/** Mueve el elemento de `from` a `to` (dentro de los límites) → lista nueva. */
export function moveTo(list, from, to) {
    const a = list.slice();
    if (from < 0 || from >= a.length) return a;
    const [x] = a.splice(from, 1);
    a.splice(Math.max(0, Math.min(a.length, to)), 0, x);
    return a;
}

/** ¿Hay algún orden propio guardado? */
export const hasOrder = (order) => !!(order && ((order.cats && order.cats.length) || Object.keys(order.chans || {}).some((k) => order.chans[k] && order.chans[k].length)));

/**
 * Mueve un canal dentro de su hilera. `base` = los ids de la hilera en su orden actual (todos), `shown` = los que se ven ahora en pantalla
 * (con filtros, pueden ser menos). where: 'first' | 'last' | 'left' | 'right' (respecto al canal vecino que se ve). → ids nuevos; si no se puede mover, los mismos.
 */
export function moveChannelIds(base, shown, id, where) {
    const ids = (base || []).map(String);
    const sid = String(id);
    if (ids.indexOf(sid) === -1) return ids;
    const rest = ids.filter((x) => x !== sid);
    if (where === 'first') return [sid].concat(rest);
    if (where === 'last') return rest.concat([sid]);
    const seen = (shown || []).map(String);
    const at = seen.indexOf(sid);
    const neighbor = at < 0 ? undefined : seen[where === 'left' ? at - 1 : at + 1];
    if (neighbor === undefined) return ids;
    const pos = rest.indexOf(neighbor);
    if (pos < 0) return ids;
    rest.splice(where === 'left' ? pos : pos + 1, 0, sid);
    return rest;
}
